import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import EgsGateResolutions from './EgsGateResolutions';
import EgsSlotDiffTable from './EgsSlotDiffTable';
import { isSlotChanged } from './buildEgsApplyPlan';
import type { EgsReviewRow } from './egsApplyRows';
import { getEgsRefusal } from './egsBlockers';
import { getEgsRowStatus } from './egsRowStatus';
import { EGS_SLOTS, EGS_SLOT_LABEL, formatAndList } from './egsUtils';
import { EGS_SELL_SOURCE_CURRENCY, type EgsWriteCurrencyMode } from './egsWriteCurrency';
import type { EgsGuestSlot } from './types';

interface Props {
  defaultExpanded: boolean;
  /** Jumps back to the step that can change this room rate's outcome, mirroring the surcharge wizard's Reselect. */
  onReselectSurcharges: () => void;
  /** Undefined where the offer has no vendor or property resolved, which is the one case with nowhere to go. */
  roomRateHref?: string;
  row: EgsReviewRow;
  setArchiveDecision: (rowId: string, decision: 'archived' | 'live') => void;
  setPayableAtProperty: (roomRateId: string, payableAtProperty: boolean) => void;
  setSlotWinner: (roomRateId: string, slot: EgsGuestSlot, rowId: string) => void;
  setWriteCurrencyMode: (roomRateId: string, mode: EgsWriteCurrencyMode) => void;
}

function StatusChip({ row }: { row: EgsReviewRow }) {
  switch (getEgsRowStatus(row.plan)) {
    case 'needsDecision':
      return <Chip color="warning" label="Needs a decision" size="small" />;

    case 'blocked':
      return <Chip color="error" label="Blocked" size="small" />;

    // Not "Blocked": nothing is wrong with this room rate, and the label goes once the rates land.
    case 'waitingOnRates':
      return <Chip color="info" label="Waiting on rates" size="small" variant="outlined" />;

    case 'noChange': {
      // A skipped guest type never reached an agreement, so "Already matches" would credit one.
      const label =
        (row.plan.isAllFreeOfCharge && row.plan.operation === 'create') || row.plan.unpricedSlots?.length
          ? 'Nothing to write'
          : 'Already matches';

      return <Chip color="default" label={label} size="small" variant="outlined" />;
    }

    default:
      return (
        <Chip color="info" label={row.plan.operation === 'create' ? 'Will be added' : 'Will change'} size="small" />
      );
  }
}

export default function EgsApplyRoomRateSection({
  defaultExpanded,
  onReselectSurcharges,
  roomRateHref,
  row,
  setArchiveDecision,
  setPayableAtProperty,
  setSlotWinner,
  setWriteCurrencyMode,
}: Props) {
  const feedingRecords = row.records.filter((record) => record.isSelected);
  // Undefined for a gate, which explains itself in the question it asks rather than in an alert above it.
  const refusal = getEgsRefusal(row.plan.blocker);
  // Empty for a record-wide refusal, which then keeps its anonymous wording.
  const blockedGuestTypes = (row.plan.blockerSlots ?? []).map((slot) => EGS_SLOT_LABEL[slot]);
  const unpricedGuestTypes = (row.plan.unpricedSlots ?? []).map((slot) => EGS_SLOT_LABEL[slot]);
  // Free of charge counts as covered: Salesforce answered for that guest type, it just answered nothing.
  const salesforceGuestTypes = EGS_SLOTS.filter((slot) => row.plan.slots[slot].origin !== 'kept').map(
    (slot) => EGS_SLOT_LABEL[slot],
  );
  // Only where the room rate is where the fix lives, or the link points the CM at the wrong system.
  const canFixOnRoomRate = refusal?.fixOn === 'roomRate' && !!roomRateHref;
  // Silent on a blocked row: `currency_mismatch` says more about the same currency than this would.
  const rewrittenFromCurrency =
    !row.plan.blocker &&
    row.plan.hasChange &&
    row.plan.existingCurrency &&
    row.plan.existingCurrency !== row.plan.writeCurrency
      ? row.plan.existingCurrency
      : undefined;
  // Not the Source column: a Salesforce figure identical to the stored one is a source without being a change.
  const changingGuestTypes = EGS_SLOTS.filter((slot) => isSlotChanged(row.plan.slots[slot])).map(
    (slot) => EGS_SLOT_LABEL[slot],
  );
  const untouchedGuestTypes = EGS_SLOTS.filter((slot) => !isSlotChanged(row.plan.slots[slot])).map(
    (slot) => EGS_SLOT_LABEL[slot],
  );
  const hasGateQuestion =
    row.plan.gates.archiveDisagreements.length > 0 ||
    !!row.plan.gates.payableConflict ||
    row.plan.gates.slotConflicts.length > 0;
  // Not `writeCurrency`: that follows the choice, so the toggle would disappear as soon as the CM picked AUD.
  const contractedCurrency = row.plan.contractedCurrency;
  const canChooseWriteCurrency = !!contractedCurrency && contractedCurrency !== EGS_SELL_SOURCE_CURRENCY;
  const canSaveInExistingCurrency =
    canChooseWriteCurrency &&
    (row.plan.existingCurrency === contractedCurrency || row.plan.existingCurrency === EGS_SELL_SOURCE_CURRENCY);
  const writeCurrencyMode: EgsWriteCurrencyMode =
    row.plan.writeCurrency === EGS_SELL_SOURCE_CURRENCY ? 'aud' : 'contracted';

  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack alignItems="center" direction="row" spacing={1} sx={{ width: '100%' }}>
          <Typography variant="body2">{`${row.roomTypeName} · ${row.ratePlanName}`}</Typography>
          <StatusChip row={row} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Typography color="text.secondary" variant="caption">
              {`From Salesforce: ${feedingRecords.map((record) => record.roomLabel).join(', ')}`}
            </Typography>
            <Button color="primary" onClick={onReselectSurcharges} size="small" variant="text">
              Change surcharges
            </Button>
          </Stack>

          {changingGuestTypes.length > 0 && (
            <Typography variant="body2">
              <strong>{`Changing ${formatAndList(changingGuestTypes)}.`}</strong>
              {untouchedGuestTypes.length > 0 &&
                ` ${formatAndList(untouchedGuestTypes)} ${untouchedGuestTypes.length === 1 ? 'keeps' : 'keep'} what the room rate already has.`}
            </Typography>
          )}

          {refusal && (
            <Alert
              action={
                canFixOnRoomRate && (
                  // A new tab: the wizard saves nothing, so navigating away here discards every answer.
                  <Button
                    color="inherit"
                    component="a"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    href={roomRateHref}
                    rel="noreferrer"
                    size="small"
                    target="_blank"
                    variant="outlined"
                  >
                    Open room rate
                  </Button>
                )
              }
              severity={refusal.severity}
            >
              {refusal.message({
                canSaveInExistingCurrency,
                existingCurrency: row.plan.existingCurrency,
                guestTypes: blockedGuestTypes,
                salesforceGuestTypes,
                writeCurrency: row.plan.writeCurrency,
              })}
            </Alert>
          )}

          {hasGateQuestion && (
            <EgsGateResolutions
              costCurrency={row.plan.writeCurrency}
              gates={row.plan.gates}
              roomRateId={row.roomRateId}
              setArchiveDecision={setArchiveDecision}
              setPayableAtProperty={setPayableAtProperty}
              setSlotWinner={setSlotWinner}
            />
          )}
          {rewrittenFromCurrency && (
            <Alert severity="warning">
              {`This room rate stores its surcharges in ${rewrittenFromCurrency}, and this offer was contracted in ${row.plan.writeCurrency}, so applying rewrites the whole record in ${row.plan.writeCurrency}. The amounts below are not a ${rewrittenFromCurrency} increase, they are ${row.plan.writeCurrency} prices.`}
            </Alert>
          )}

          {/* Named even on a blocked row: it explains why a guest type is absent from the diff either way. */}
          {unpricedGuestTypes.length > 0 && (
            <Alert severity="warning">
              {`${unpricedGuestTypes.length === 1 ? `The ${unpricedGuestTypes[0]} row` : `The ${formatAndList(unpricedGuestTypes)} rows`} from Salesforce ${unpricedGuestTypes.length === 1 ? 'charges' : 'charge'} for the guest type without naming a price, so ${unpricedGuestTypes.length === 1 ? 'it keeps' : 'they keep'} what the room rate already has. Add the Sell Price (AUD) in Salesforce and apply again to include ${unpricedGuestTypes.length === 1 ? 'it' : 'them'}.`}
            </Alert>
          )}

          {/* Silent where a guest type was skipped, since "Salesforce matches everything" would then be untrue. */}
          {!row.plan.blocker && !row.plan.hasChange && !row.plan.isAllFreeOfCharge && !unpricedGuestTypes.length && (
            <Alert severity="info">
              Salesforce matches every amount already saved here, so nothing is written. The Source column below says
              where each amount came from, not that it is changing.
            </Alert>
          )}
          {!row.plan.blocker && row.plan.operation === 'create' && row.plan.isAllFreeOfCharge && (
            <Alert severity="info">
              Every guest type on this room is Free of Charge, so there is nothing to write.
            </Alert>
          )}
          {canChooseWriteCurrency && (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">Save this room rate in</Typography>
              <ToggleButtonGroup
                color="primary"
                exclusive
                onChange={(_event, value) => value && setWriteCurrencyMode(row.roomRateId, value)}
                size="small"
                value={writeCurrencyMode}
              >
                <ToggleButton value="contracted">{contractedCurrency}</ToggleButton>
                <ToggleButton value="aud">{EGS_SELL_SOURCE_CURRENCY}</ToggleButton>
              </ToggleButtonGroup>
              <Typography color="text.secondary" variant="caption">
                {`This offer was contracted in ${contractedCurrency}, and Salesforce prices every guest type in ${EGS_SELL_SOURCE_CURRENCY}. ${contractedCurrency}: the cost is exact and the sell is converted at today's rate. ${EGS_SELL_SOURCE_CURRENCY}: the sell is exact and the cost is Salesforce's rounded ${EGS_SELL_SOURCE_CURRENCY} figure.`}
              </Typography>
            </Stack>
          )}
          <EgsSlotDiffTable plan={row.plan} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
